import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ProductRecommendations from "@/components/ProductRecommendations.jsx";
import LoginModal from "@/components/LoginModal.jsx";
import FavoriteHeartButton from "@/components/FavoriteHeartButton.jsx";
import ShareIcon from "@/components/ShareIcon.jsx";
import BackLink from "@/components/BackLink.jsx";
import { getProduct } from "@/api/public.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import { useCart } from "@/cart/CartContext.jsx";
import { CART_ROUTE, MAIN_ROUTE, PRODUCT_PAGE_TITLE_SUFFIX, PRODUCT_REVIEWS_ROUTE, ROLES, STORE_NAME } from "@/utils/constants.js";
import { formatStock, formatRatingOutOfFive } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { formatVolumePriceTiers, formatVolumeRangeLabel, resolveVolumeUnitPrice } from "@/utils/productPricing.js";
import { buildProductShareUrl, shareProduct } from "@/utils/shareProduct.js";
import ProductImageGallery from "@/components/ProductImageGallery.jsx";
import CountryOriginBanner from "@/components/CountryOriginBanner.jsx";
import "./product.css";
import "./product-reviews.css";

export default function ProductPage() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [quantityInput, setQuantityInput] = useState("1");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [adding, setAdding] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [shareFeedback, setShareFeedback] = useState("");
    const { isAuthenticated, role } = useAuth();
    const { items, setCartQuantity } = useCart();

    const cartQuantity = useMemo(() => {
        if (!isAuthenticated || role !== ROLES.CUSTOMER) {
            return null;
        }
        const item = items.find((entry) => String(entry.productId) === String(id));
        return item?.quantity > 0 ? item.quantity : null;
    }, [items, id, isAuthenticated, role]);

    const alreadyInCart = cartQuantity != null;

    useEffect(() => {
        getProduct(id).then((data) => setProduct(data.data)).catch((err) => setError(err.message));
    }, [id]);

    useEffect(() => {
        if (!product?.title) {
            return undefined;
        }
        document.title = `${product.title} — ${PRODUCT_PAGE_TITLE_SUFFIX}`;
        return () => {
            document.title = STORE_NAME;
        };
    }, [product?.title]);

    useEffect(() => {
        setQuantityInput(String(cartQuantity ?? 1));
    }, [id, cartQuantity]);

    useEffect(() => {
        setMessage("");
        setError("");
        setShareFeedback("");
    }, [id]);

    const handleShareProduct = async () => {
        if (!product) {
            return;
        }
        setShareFeedback("");
        try {
            const result = await shareProduct({
                title: product.title,
                url: buildProductShareUrl(product.id)
            });
            if (result === "copied") {
                setShareFeedback("Lien copié");
            }
        } catch {
            setShareFeedback("Partage indisponible");
        }
    };

    const parsedQuantity = Number(quantityInput);
    const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : null;
    const quantityForPrice = quantity ?? 1;
    const unitPrice = product ? resolveVolumeUnitPrice(product, quantityForPrice) : 0;
    const volumeTiers = product ? formatVolumePriceTiers(product.volumePrices) : [];
    const defaultQuantityLabel = String(cartQuantity ?? 1);

    const handleAddToCart = async () => {
        if (!isAuthenticated) {
            setShowLogin(true);
            return;
        }
        if (role !== ROLES.CUSTOMER) {
            setError("Seuls les comptes client peuvent ajouter au panier");
            return;
        }
        if (!product) {
            setError("Produit introuvable");
            return;
        }
        if (quantity == null) {
            setError("Veuillez saisir une quantité d'au moins 1.");
            return;
        }
        if (quantity > product.stockQuantity) {
            const stock = product.stockQuantity;
            setError(
                stock <= 0
                    ? "Ce produit est en rupture de stock."
                    : `Stock insuffisant : seulement ${stock} article${stock > 1 ? "s" : ""} disponible${stock > 1 ? "s" : ""}.`
            );
            return;
        }
        setAdding(true);
        setError("");
        setMessage("");
        try {
            await setCartQuantity(id, quantity);
            setMessage(
                alreadyInCart
                    ? `Panier mis à jour (${quantity} article${quantity > 1 ? "s" : ""})`
                    : `Ajouté au panier (${quantity} article${quantity > 1 ? "s" : ""})`
            );
        } catch (err) {
            setError(err.message);
        } finally {
            setAdding(false);
        }
    };

    if (!product && !error) {
        return <p className="container muted">Chargement du produit...</p>;
    }

    return (
        <>
            <main className="container product-page">
                <div className="product-page-toolbar">
                    <BackLink to={MAIN_ROUTE} className="product-page-back">
                        Retour au catalogue
                    </BackLink>
                    {product && (
                        <div className="product-page-actions">
                            <FavoriteHeartButton productId={product.id} variant="toolbar" />
                            <div className="product-page-share-wrap">
                                <button
                                    type="button"
                                    className="btn btn-secondary product-page-share"
                                    onClick={handleShareProduct}
                                    aria-label={`Partager ${product.title}`}
                                >
                                    <span className="product-page-action-label">Partager</span>
                                    <ShareIcon className="product-page-share-icon" />
                                </button>
                                {shareFeedback && (
                                    <span className="product-page-share-feedback muted" role="status">
                                        {shareFeedback}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {error && !product && <p className="error">{error}</p>}
                {product && (
                    <div className="product-detail card">
                        <div className="product-detail-gallery-wrap">
                            {product.showCountryBannerOnDetail && product.countryOfOrigin && (
                                <CountryOriginBanner country={product.countryOfOrigin} size="md" />
                            )}
                            <ProductImageGallery
                                key={product.id}
                                photoIds={product.photoIds?.length ? product.photoIds : (product.photoId ? [product.photoId] : [])}
                                title={product.title}
                                placeholder={product.category?.[0] || "P"}
                            />
                        </div>
                        <div className="product-detail-body">
                            <section className="product-detail-section product-detail-section--identity">
                                <span className="product-category">{product.category}</span>
                                <h1>{product.title}</h1>
                                {product.description && (
                                    <p className="product-detail-description">{product.description}</p>
                                )}
                            </section>

                            <section className="product-detail-section product-detail-section--price" aria-label="Prix">
                                <div className="price-row">
                                    <strong><Price value={unitPrice} /></strong>
                                    <span className="muted">{formatStock(product.stockQuantity)}</span>
                                </div>
                                {product.hasVolumePricing && volumeTiers.length > 0 && (
                                    <div className="product-volume-pricing muted">
                                        <p className="product-detail-section-label">Tarif dégressif</p>
                                        <ul>
                                            {volumeTiers.map((tier) => (
                                                <li key={`${tier.minQuantity}-${tier.maxQuantity ?? "inf"}`}>
                                                    {formatVolumeRangeLabel(tier)} : <Price value={tier.price} /> / unité
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </section>

                            <section className="product-detail-section product-detail-section--reviews" aria-label="Avis">
                                <div className="product-review-summary">
                                    {(product.reviewCount ?? 0) > 0 ? (
                                        <div className="product-review-summary-meta">
                                            <strong>{formatRatingOutOfFive(product.averageGrade ?? 0)}</strong>
                                            <span className="review-stars" aria-hidden="true">
                                                {"★".repeat(Math.round(Number(product.averageGrade) || 0))}
                                                <span className="review-stars-empty">
                                                    {"★".repeat(5 - Math.round(Number(product.averageGrade) || 0))}
                                                </span>
                                            </span>
                                            <span className="muted">
                                                {product.reviewCount} avis
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="muted">Pas encore d&apos;avis</span>
                                    )}
                                    {(product.reviewCount ?? 0) > 0 && (
                                        <Link
                                            to={PRODUCT_REVIEWS_ROUTE(product.id)}
                                            className="btn btn-secondary btn-inline product-review-summary-action"
                                        >
                                            Voir les avis
                                        </Link>
                                    )}
                                </div>
                            </section>

                            <section className="product-detail-section product-detail-section--purchase" aria-label="Commander">
                                <label className="field product-quantity-field">
                                    Quantité
                                    <input
                                        type="number"
                                        min="1"
                                        max={product.stockQuantity}
                                        inputMode="numeric"
                                        value={quantityInput}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            // Allow empty while typing so the user can replace the value with another digit.
                                            if (raw === "") {
                                                setQuantityInput("");
                                                setError("");
                                                return;
                                            }
                                            if (/^\d+$/.test(raw)) {
                                                setQuantityInput(raw);
                                                setError("");
                                            }
                                        }}
                                        onBlur={() => {
                                            if (quantity == null) {
                                                setQuantityInput(defaultQuantityLabel);
                                            }
                                        }}
                                        disabled={product.stockQuantity <= 0 || adding}
                                    />
                                </label>
                                {alreadyInCart && (
                                    <p className="muted product-detail-note">
                                        Déjà dans votre panier — la quantité ci-dessus sera celle du panier.
                                    </p>
                                )}
                                {product.hasVolumePricing && (
                                    <p className="muted product-detail-note">
                                        Sous-total indicatif : <Price value={unitPrice * quantityForPrice} />
                                    </p>
                                )}
                                <div className="product-actions">
                                    <button
                                        className="btn btn-primary btn-inline"
                                        onClick={handleAddToCart}
                                        disabled={product.stockQuantity <= 0 || adding}
                                    >
                                        {adding
                                            ? (alreadyInCart ? "Mise à jour..." : "Ajout...")
                                            : (alreadyInCart ? "Mettre à jour le panier" : "Ajouter au panier")}
                                    </button>
                                    {isAuthenticated && role === ROLES.CUSTOMER && (
                                        <Link to={CART_ROUTE} className="btn btn-secondary btn-inline">
                                            Voir le panier
                                        </Link>
                                    )}
                                </div>
                                {message && <p className="success">{message}</p>}
                                {error && <p className="error">{error}</p>}
                            </section>
                        </div>
                    </div>
                )}
                {isAuthenticated && role === ROLES.CUSTOMER && product && (
                    <ProductRecommendations
                        excludeProductId={product.id}
                        resetKey={product.id}
                    />
                )}
            </main>
            {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
        </>
    );
}
